/* eslint-disable react/prop-types */
import { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import { Stack } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import TableCommon from "components/TableCommon";

function DataTable({
  table,
  pagination,
  isSorted,
  noEndBorder,
  setQuery,
  query,
  paginationData,
  setPaginationData,
  loading,
  onOpenCreateDialog,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { setPageSize } = tableInstance;

  useEffect(() => setPageSize(paginationData.size), [paginationData.size, setPageSize]);

  const [year, setYear] = useState(query?.year || String(new Date().getFullYear()));
  const [fromDate, setFromDate] = useState(query?.fromDate || "");
  const [toDate, setToDate] = useState(query?.toDate || "");

  useEffect(() => {
    setYear(query?.year || String(new Date().getFullYear()));
    setFromDate(query?.fromDate || "");
    setToDate(query?.toDate || "");
  }, [query]);

  return (
    <>
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        p={3}
        pb={1}
      >
        <Stack spacing={1} direction="row" mb={2}>
          <SoftButton variant="gradient" color="dark" size="small" onClick={onOpenCreateDialog}>
            TẠO NHẬT KÝ
          </SoftButton>
        </Stack>

        <SoftBox lineHeight={1} width="100%">
          <Stack spacing={2} direction="row" flexWrap="wrap">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Năm
              </SoftTypography>
              <SoftBox width="8rem">
                <SoftInput
                  placeholder="YYYY"
                  value={year}
                  onChange={({ currentTarget }) => {
                    const nextValue = currentTarget.value.replace(/\D/g, "").slice(0, 4);
                    setYear(nextValue);
                    setPaginationData((prev) => ({ ...prev, page: 1 }));
                    setQuery((prev) => ({ ...prev, year: nextValue }));
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Từ ngày tạo
              </SoftTypography>
              <SoftBox width="11rem">
                <SoftInput
                  type="date"
                  value={fromDate}
                  onChange={({ currentTarget }) => {
                    setFromDate(currentTarget.value);
                    setPaginationData((prev) => ({ ...prev, page: 1 }));
                    setQuery((prev) => ({ ...prev, fromDate: currentTarget.value || "" }));
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Đến ngày tạo
              </SoftTypography>
              <SoftBox width="11rem">
                <SoftInput
                  type="date"
                  value={toDate}
                  onChange={({ currentTarget }) => {
                    setToDate(currentTarget.value);
                    setPaginationData((prev) => ({ ...prev, page: 1 }));
                    setQuery((prev) => ({ ...prev, toDate: currentTarget.value || "" }));
                  }}
                />
              </SoftBox>
            </SoftBox>
          </Stack>
        </SoftBox>
      </SoftBox>

      <TableCommon
        loading={loading}
        query={query}
        setQuery={setQuery}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        table={table}
        isSorted={isSorted}
        noEndBorder={noEndBorder}
        pagination={pagination}
        tableInstance={tableInstance}
      />
    </>
  );
}

DataTable.propTypes = {
  query: PropTypes.any,
  setQuery: PropTypes.any,
  onOpenCreateDialog: PropTypes.func,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  onOpenCreateDialog: () => {},
};

export default DataTable;
